import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Loader2, RefreshCw, Save, SearchCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { SolonTabsView } from "./SolonTabsView";

const API = process.env.REACT_APP_BACKEND_URL;
const SOLON_URL = "https://extapps.solon.gov.gr/mojwp/faces/TrackLdoPublic";

const fmt = (v) => (v === true ? "ΝΑΙ" : v === false ? "ΟΧΙ" : v == null || v === "" ? "—" : String(v));

const MiniTable = ({ title, rows, cols }) => {
  if (!rows?.length) return null;
  return (
    <div className="mt-3">
      <div className="text-xs font-semibold text-slate-700 mb-1">{title}</div>
      <table className="w-full text-xs border rounded">
        <thead className="bg-slate-50"><tr>{cols.map(([k, l]) => <th key={k} className="text-left px-2 py-1 font-medium text-slate-600">{l}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t">{cols.map(([k]) => <td key={k} className="px-2 py-1">{fmt(r[k])}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
};

/** «ΕΛΕΓΧΟΣ ΣΤΟ SOLON.GOV» → σύγκριση → «ΕΝΗΜΕΡΩΣΗ ΣΤΟΙΧΕΙΩΝ ΥΠΟΘΕΣΗΣ». */
export const SolonCheckButton = ({ caseId, caseData, onUpdated, size = "sm", variant = "outline" }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});

  const check = async () => {
    setLoading(true); setOpen(true); setData(null);
    try {
      const r = await axios.post(`${API}/api/cases/${caseId}/solon/check`);
      setData(r.data);
      const sel = {};
      (r.data.proposed || []).forEach((p) => { if (p.changed) sel[p.field] = true; });
      setSelected(sel);
      if (!r.data.found) toast.info("Η υπόθεση δεν βρέθηκε στο SOLON.GOV");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα επικοινωνίας με το SOLON.GOV");
      setOpen(false);
    } finally { setLoading(false); }
  };

  const apply = async () => {
    const fields = Object.keys(selected).filter((k) => selected[k]);
    if (!fields.length) return toast.error("Επιλέξτε τουλάχιστον ένα πεδίο");
    setApplying(true);
    try {
      const r = await axios.post(`${API}/api/cases/${caseId}/solon/apply`, { fields });
      toast.success(`Ενημερώθηκαν ${r.data.updated_fields.length} πεδία από το SOLON.GOV`);
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα ενημέρωσης");
    } finally { setApplying(false); }
  };

  const proposed = data?.proposed || [];
  const changed = proposed.filter((p) => p.changed);
  const d = data?.details;

  return (
    <>
      <Button size={size} variant={variant} onClick={check} disabled={loading} data-testid="solon-check-btn" className="gap-1.5">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchCheck className="w-4 h-4" />} Έλεγχος στο SOLON.GOV
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="solon-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">Έλεγχος στο SOLON.GOV — ΓΑΚ {caseData?.gak_number}/{caseData?.gak_year}
              {data && <Badge variant={data.found ? "default" : "secondary"} className={data.found ? "bg-emerald-600" : ""} data-testid="solon-found-badge">{data.found ? "Βρέθηκε" : "Δεν βρέθηκε"}</Badge>}
            </DialogTitle>
            <DialogDescription>Κατάστημα: {data?.court_office || "…"} · Πηγή: <a href={SOLON_URL} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Πορεία Υπόθεσης (Πολιτική) <ExternalLink className="w-3 h-3" /></a>
              {data?.fetched_at && <> · Άντληση: {new Date(data.fetched_at).toLocaleString("el-GR")}</>}</DialogDescription>
          </DialogHeader>
          {loading && <div className="py-10 flex items-center justify-center gap-2 text-slate-500" data-testid="solon-loading"><Loader2 className="w-5 h-5 animate-spin" /> Άντληση στοιχείων από το SOLON.GOV…</div>}
          {data?.found && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-slate-800">Σύγκριση στοιχείων</div>
              <table className="w-full text-sm border rounded" data-testid="solon-compare-table">
                <thead className="bg-slate-50"><tr><th className="w-8"></th><th className="text-left px-2 py-1.5 font-medium">Πεδίο</th><th className="text-left px-2 py-1.5 font-medium">Τρέχουσα τιμή</th><th className="text-left px-2 py-1.5 font-medium">SOLON.GOV</th></tr></thead>
                <tbody>
                  {proposed.map((p) => (
                    <tr key={p.field} className={`border-t ${p.changed ? "bg-amber-50/60" : ""}`} data-testid={`solon-row-${p.field}`}>
                      <td className="px-2 py-1.5 text-center">
                        <input type="checkbox" disabled={!p.changed} checked={!!selected[p.field]} onChange={(e) => setSelected({ ...selected, [p.field]: e.target.checked })} data-testid={`solon-select-${p.field}`} />
                      </td>
                      <td className="px-2 py-1.5 text-slate-700">{p.label}</td>
                      <td className="px-2 py-1.5 text-slate-500">{fmt(p.current)}</td>
                      <td className={`px-2 py-1.5 ${p.changed ? "font-semibold text-slate-900" : "text-slate-500"}`}>{fmt(p.proposed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!changed.length && <p className="text-xs text-emerald-700" data-testid="solon-no-diff">Όλα τα στοιχεία συμφωνούν με το SOLON.GOV.</p>}
              <MiniTable title="Λίστα Δικηγόρων Κατάθεσης" rows={d?.lawyers} cols={[["bar", "Δικηγορικός Σύλλογος"], ["registry_no", "Α.Μ."], ["surname", "Επώνυμο"], ["name", "Όνομα"]]} />
              <MiniTable title="Στοιχεία Εναγόντων" rows={d?.plaintiffs} cols={[["seq", "Α/Α"], ["category", "Κατηγορία"], ["surname", "Επώνυμο/Επωνυμία"], ["name", "Όνομα"], ["father_name", "Πατρώνυμο"]]} />
              <MiniTable title="Στοιχεία Εναγομένων" rows={d?.defendants} cols={[["seq", "Α/Α"], ["category", "Κατηγορία"], ["surname", "Επώνυμο/Επωνυμία"], ["name", "Όνομα"], ["father_name", "Πατρώνυμο"]]} />
              {d?.tabs && (
                <div className="mt-4 space-y-2">
                  <div className="text-sm font-semibold text-slate-800">Λεπτομέρειες Υπόθεσης (9 καρτέλες SOLON)</div>
                  <SolonTabsView tabs={d.tabs} />
                </div>
              )}
            </div>
          )}
          {data && !data.found && <p className="text-sm text-slate-600 py-4" data-testid="solon-not-found">Δεν βρέθηκε εγγραφή για ΓΑΚ {caseData?.gak_number}/{caseData?.gak_year} στο επιλεγμένο Κατάστημα. Ελέγξτε τον αριθμό/έτος ή το Κατάστημα (Διαχείριση → Εργαλεία → SOLON.GOV).</p>}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={check} disabled={loading} data-testid="solon-recheck-btn"><RefreshCw className="w-4 h-4 mr-1" />Επανέλεγχος</Button>
            <Button onClick={apply} disabled={applying || !data?.found || !changed.length} data-testid="solon-apply-btn">
              {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Ενημέρωση Στοιχείων Υπόθεσης
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
